import axios from 'axios';
import { getKeycloak } from '../config/keycloak';

const rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const normalisedApiUrl = (() => {
  const trimmed = rawApiUrl.replace(/\/$/, '');
  if (trimmed.endsWith('/api/v1')) return trimmed;
  if (trimmed.endsWith('/api')) return `${trimmed}/v1`;
  return `${trimmed}/api/v1`;
})();

const api = axios.create({
  baseURL: normalisedApiUrl,
});

api.interceptors.request.use(
  (config) => {
    const keycloak = getKeycloak();
    if (keycloak?.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const reportingApi = {
  // --- Configuration ---
  getSettings: () =>
    api.get('/reporting/settings'),

  updateSettings: (payload) =>
    api.put('/reporting/settings', payload),

  // --- Tables de référence ---
  initTables: () =>
    api.post('/reporting/init-tables'),

  getReferenceCounts: () =>
    api.get('/reporting/reference-counts'),

  getFichiers: () =>
    api.get('/reporting/fichiers'),

  getPostes: (fichier, decs) =>
    api.get('/reporting/postes', { params: { fichier, decs } }),

  // --- Production ---
  produce: (payload) =>
    api.post('/reporting/produce', payload),

  getRuns: (limit = 20) =>
    api.get('/reporting/runs', { params: { limit } }),

  // --- Consultation ---
  getData: (closingDate, fichier, decs, manuel = false) =>
    api.get('/reporting/data', { params: { closing_date: closingDate, fichier, decs, manuel } }),

  // --- Cache Amplitude ---
  getFichiersForDate: (closingDate) =>
    api.get('/reporting/fichiers-for-date', { params: { closing_date: closingDate } }),

  getCacheStatus: () =>
    api.get('/reporting/cache-status'),

  purgeCache: (closingDate = null) =>
    api.delete('/reporting/cache', {
      params: closingDate ? { closing_date: closingDate } : {},
    }),

  purgeAll: (closingDate = null, fichier = null) =>
    api.delete('/reporting/purge-all', {
      params: {
        ...(closingDate ? { closing_date: closingDate } : {}),
        ...(fichier ? { fichier } : {}),
      },
    }),

  // --- Déclarations manuelles ---
  uploadManual: (file, closingDate) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/upload-manual', formData, {
      params: { closing_date: closingDate },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getManualDeclarations: (closingDate = null) =>
    api.get('/reporting/manual-declarations', {
      params: closingDate ? { closing_date: closingDate } : {},
    }),

  getManualDates: () =>
    api.get('/reporting/manual-dates'),

  getReimportDates: () =>
    api.get('/reporting/reimport-dates'),

  getReimportFichiers: (closingDate) =>
    api.get('/reporting/reimport-fichiers', { params: { closing_date: closingDate } }),

  getModifiedFichiers: (closingDate) =>
    api.get('/reporting/modified-fichiers', { params: { closing_date: closingDate } }),

  // --- Export TXT / Excel COBAC ---

  exportTxt: (closingDate, fichier, decs, manuel = false) =>
    api.get('/reporting/export-txt', {
      params: { closing_date: closingDate, fichier, decs, manuel },
      responseType: 'blob',
    }),

  exportExcel: (closingDate, fichier, decs, manuel = false) =>
    api.get('/reporting/export-excel', {
      params: { closing_date: closingDate, fichier, decs, manuel },
      responseType: 'blob',
    }),

  exportZip: (closingDate) =>
    api.get('/reporting/export-zip', {
      params: { closing_date: closingDate },
      responseType: 'blob',
    }),

  exportZipSelected: (closingDate, files) =>
    api.get('/reporting/export-zip-selected', {
      params: { closing_date: closingDate, files: files.join(',') },
      responseType: 'blob',
    }),

  exportTxtPost: (payload) =>
    api.post('/reporting/export-txt', payload, { responseType: 'blob' }),

  exportExcelPost: (payload) =>
    api.post('/reporting/export-excel', payload, { responseType: 'blob' }),

  // --- CID (Contrôles Intra/Inter-Documents) ---
  cidRun: (closingDate, fichier, decs = '', includeCinter = true) =>
    api.get(`/reporting/cid/${closingDate}/${fichier}/run`, {
      params: { decs, ...(includeCinter === false ? { include_cinter: false } : {}) },
    }),

  cidCorrectManual: (closingDate, fichier, payload) =>
    api.post(`/reporting/cid/${closingDate}/${fichier}/correct/manual`, payload),

  cidCorrectAuto: (closingDate, fichier, payload, decs = '') =>
    api.post(`/reporting/cid/${closingDate}/${fichier}/correct/auto`, payload, { params: { decs } }),

  cidGetLog: (closingDate, fichier) =>
    api.get(`/reporting/cid/${closingDate}/${fichier}/log`),

  cidRevert: (logId) =>
    api.post(`/reporting/cid/revert/${logId}`),

  cidBatchRevert: (closingDate, fichier, actionFilter = null) =>
    api.post(`/reporting/cid/${closingDate}/${fichier}/revert-batch`, {
      ...(actionFilter ? { action_filter: actionFilter } : {}),
    }),

  cidSaveEdits: (closingDate, fichier, edits) =>
    api.patch(`/reporting/cid/${closingDate}/${fichier}/values`, { edits }),

  // Pré-chauffe globale : génère sm_declaration_values pour tous les fichiers CID/CCplex
  // À appeler à l'ouverture du module Reporting (idempotent, en arrière-plan).
  cidWarm: (closingDate) =>
    api.post(`/reporting/cid/${closingDate}/warm`),

  cidWarmStatus: (closingDate) =>
    api.get(`/reporting/cid/${closingDate}/warm/status`),

  // Correction automatique complète multi-passes
  cidFullCorrect: (closingDate, decs = '', maxIterations = 10) =>
    api.post(`/reporting/cid/${closingDate}/correct/full`, { decs, max_iterations: maxIterations }),

  // --- Corrections automatiques (pipeline aligné expert) ---
  cerberPipeline: (closingDate, decs = '', maxIterations = 10, options = {}) =>
    api.post(`/reporting/cid/${closingDate}/cerber/pipeline`, {
      decs,
      max_iterations: maxIterations,
      run_cquali: options.runCquali ?? true,
      run_f1000_balance: options.runF1000Balance ?? true,
      run_cid: options.runCid ?? true,
      use_unified: options.useUnified ?? true,
    }),

  // --- Import ZIP COBAC (simple sauvegarde en base) ---
  uploadZipManual: (closingDate, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/upload-zip-manual', formData, {
      params: { closing_date: closingDate },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // --- Réimport de fichiers exportés (écrasement valeurs auto) ---

  importTxt: (file, closingDate, decs = '') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/import-txt', formData, {
      params: { closing_date: closingDate, decs },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importExcel: (file, closingDate) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/import-excel', formData, {
      params: { closing_date: closingDate },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importZip: (file, closingDate) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/import-zip', formData, {
      params: { closing_date: closingDate },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // --- CERBER : pipeline complet sur ZIP COBAC uploadé ---
  cerberProcessZip: (file, closingDate = null, saveToDb = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = { save_to_db: saveToDb };
    if (closingDate) params.closing_date = closingDate;
    return api.post('/reporting/cerber/process-zip', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // --- Import manuel CSV (SQL Developer) ---
  downloadSql: (closingDate, previousClosingDate) =>
    api.get('/reporting/download-sql', {
      params: { closing_date: closingDate, previous_closing_date: previousClosingDate },
      responseType: 'blob',
    }),

  importCsv: (file, closingDate, previousClosingDate, skipAutoCorrections = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/import-csv', formData, {
      params: {
        closing_date: closingDate,
        previous_closing_date: previousClosingDate,
        skip_auto_corrections: skipAutoCorrections,
      },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importTransfertCsv: (file, closingDate) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/import-transfert-csv', formData, {
      params: { closing_date: closingDate },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importBktauCsv: (file, closingDate) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reporting/import-bktau-csv', formData, {
      params: { closing_date: closingDate },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // --- Journal d'audit ---
  getAuditLogs: (params = {}) =>
    api.get('/reporting/audit-logs', { params }),

  // --- Validation des déclarations ---
  getDeclarationStatus: (closingDate) =>
    api.get(`/reporting/declarations/${closingDate}/status`),

  validateDeclaration: (closingDate, validated) =>
    api.post(`/reporting/declarations/${closingDate}/validate`, { validated }),

  purgeFichiers: (fichiers) =>
    api.post('/reporting/purge-fichiers', { fichiers }),
};
